import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle2, Circle, Clock, AlertTriangle,
  ChevronDown, ChevronRight, Sparkles, Shield, Download,
  FileText, FolderSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { WorkflowTask, WorkflowChecklistItem, PolicyReviewSession } from '@/types/api';

const STATUS_ICONS: Record<string, typeof Circle> = {
  pending: Circle,
  assigned: Clock,
  in_progress: Clock,
  review: AlertTriangle,
  completed: CheckCircle2,
  blocked: AlertTriangle,
  escalated: AlertTriangle,
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'text-muted-foreground',
  assigned: 'text-blue-500',
  in_progress: 'text-teal',
  review: 'text-compliance-medium',
  completed: 'text-compliance-low',
  blocked: 'text-compliance-critical',
  escalated: 'text-compliance-high',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'text-compliance-critical',
  high: 'text-compliance-high',
  medium: 'text-compliance-medium',
  low: 'text-compliance-low',
};

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['workflow-project', projectId],
    queryFn: () => api.projects.get(projectId!),
    enabled: !!projectId && !!user,
  });

  const { data: tasksData } = useQuery({
    queryKey: ['workflow-tasks', projectId],
    queryFn: () => api.tasks.list({ projectId }),
    enabled: !!projectId && !!user,
  });

  const tasks = tasksData?.tasks ?? [];

  const { data: policyReviewData } = useQuery({
    queryKey: ['policy-review-sessions', projectId],
    queryFn: () => api.policyReview.sessions({ projectId }),
    enabled: !!projectId && !!user,
  });
  const policySessions = policyReviewData?.sessions ?? [];

  const generateChecklistMutation = useMutation({
    mutationFn: (taskId: string) =>
      api.ai.generateChecklist(taskId),
    onSuccess: (_data, taskId) => {
      toast.success('Checklist generated by AI');
      queryClient.invalidateQueries({ queryKey: ['workflow-checklist', taskId] });
    },
    onError: () => toast.error('Failed to generate checklist'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      api.tasks.update(taskId, { status }),
    onSuccess: (_data, vars) => {
      const label = vars.status === 'cancelled' ? 'Project cancelled' : `Status updated to ${vars.status.replace('_', ' ')}`;
      toast.success(label);
      queryClient.invalidateQueries({ queryKey: ['workflow-tasks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-project', projectId] });
    },
    onError: () => toast.error('Failed to update status'),
  });

  const closeCancelConfirm = useCallback(() => setShowCancelConfirm(false), []);
  const cancelDialogRef = useFocusTrap(showCancelConfirm, closeCancelConfirm);

  if (projectLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal border-t-transparent" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/projects')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <p className="mt-4 text-muted-foreground">Project not found.</p>
      </div>
    );
  }

  const isOverdue = project.deadline && new Date(project.deadline) < new Date() && project.status !== 'completed';

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/projects')}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Projects
        </Button>
      </div>

      <PageHeader
        title={project.title}
        subtitle={project.description ?? undefined}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(api.reports.projectPdf(project.projectId), '_blank')}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              Export PDF
            </Button>
            {project.status !== 'completed' && project.status !== 'cancelled' && !showCancelConfirm && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelConfirm(true)}
              >
                Cancel Project
              </Button>
            )}
            {showCancelConfirm && (
              <div ref={cancelDialogRef} className="flex items-center gap-2 rounded border border-compliance-critical/30 bg-compliance-critical/5 px-3 py-1.5" role="dialog" aria-label="Cancel project confirmation">
                <span className="text-xs font-medium text-compliance-critical">Cancel this project?</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 border-compliance-critical/50 text-xs text-compliance-critical hover:bg-compliance-critical hover:text-white"
                  onClick={() => {
                    statusMutation.mutate({ taskId: project.projectId, status: 'cancelled' });
                    setShowCancelConfirm(false);
                  }}
                >
                  Yes, Cancel
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => setShowCancelConfirm(false)}
                >
                  No
                </Button>
              </div>
            )}
          </div>
        }
      />

      {/* Project meta */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Status</p>
            <p className="mt-1 text-lg font-semibold capitalize">{project.status.replace('_', ' ')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Priority</p>
            <p className={cn('mt-1 text-lg font-semibold capitalize', PRIORITY_COLORS[project.priority])}>
              {project.priority}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Progress</p>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-lg font-semibold">{project.tasksCompleted}/{project.taskCount}</span>
              <Progress value={project.progressPct} className="h-2 flex-1" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Deadline</p>
            <p className={cn('mt-1 text-lg font-semibold', isOverdue && 'text-compliance-critical')}>
              {project.deadline ? new Date(project.deadline).toLocaleDateString() : 'None'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* AI Summary */}
      {project.summaryAi && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-purple-800">
              <Sparkles className="h-4 w-4" />
              AI Summary
            </div>
            <p className="mt-1 text-sm text-purple-900">{project.summaryAi}</p>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        {project.frameworks.map((fw) => (
          <Badge key={fw} variant="outline" className="bg-teal/10 text-teal-light">{fw}</Badge>
        ))}
        {project.departments.map((dept) => (
          <Badge key={dept} variant="outline">{dept}</Badge>
        ))}
        {project.createdByType === 'agent' && (
          <Badge variant="outline" className="bg-purple-100 text-purple-800">AI Generated</Badge>
        )}
      </div>

      {/* Policy Documents */}
      {policySessions.length > 0 && (
        <div>
          <h2 className="mb-3 text-lg font-semibold text-foreground">
            Policy Documents ({policySessions.length})
          </h2>
          <div className="space-y-2">
            {policySessions.map((ps: PolicyReviewSession) => {
              const reviewed = ps.acceptedCount + ps.rejectedCount;
              const statusStyle: Record<string, string> = {
                pending: 'bg-muted text-muted-foreground',
                in_review: 'bg-teal/15 text-teal-light',
                approved: 'bg-compliance-low/15 text-compliance-low',
                written_back: 'bg-purple-100 text-purple-800',
              };
              const statusLabel: Record<string, string> = {
                pending: 'Pending Review',
                in_review: 'In Review',
                approved: 'Approved',
                written_back: 'Written Back',
              };
              return (
                <Card key={ps.sessionId}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{ps.documentName}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className={cn('text-[10px]', statusStyle[ps.status])}>
                          {statusLabel[ps.status] ?? ps.status}
                        </Badge>
                        {ps.sectionCount > 0 && (
                          <span>{reviewed}/{ps.sectionCount} sections reviewed</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 text-xs"
                      onClick={() => navigate(`/policy-review/${ps.sessionId}`)}
                    >
                      <FolderSearch className="mr-1 h-3 w-3" />
                      Review
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Tasks */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-foreground">
          Tasks ({tasks.length})
        </h2>
        {tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tasks yet.</p>
        ) : (
          <div className="space-y-2">
            {tasks.map((task) => (
              <TaskRow
                key={task.taskId}
                task={task}
                onStatusChange={(status) => statusMutation.mutate({ taskId: task.taskId, status })}
                onGenerateChecklist={() => generateChecklistMutation.mutate(task.taskId)}
                isGenerating={generateChecklistMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRow({
  task,
  onStatusChange,
  onGenerateChecklist,
  isGenerating,
}: {
  readonly task: WorkflowTask;
  readonly onStatusChange: (status: string) => void;
  readonly onGenerateChecklist: () => void;
  readonly isGenerating: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const StatusIcon = STATUS_ICONS[task.status] ?? Circle;

  return (
    <Card>
      <CardContent className="p-0">
        <div
          className="flex cursor-pointer items-center gap-3 p-3"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <StatusIcon className={cn('h-4 w-4 shrink-0', STATUS_COLORS[task.status])} />
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-foreground">{task.title}</span>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded bg-muted px-1.5 py-0.5">{task.department}</span>
              <span>{task.taskType}</span>
              {task.checklistTotal > 0 && (
                <span>{task.checklistDone}/{task.checklistTotal}</span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className={cn('text-xs font-semibold', PRIORITY_COLORS[task.priority])}>
              {task.priority.toUpperCase()}
            </span>
            {task.dueDate && (
              <span className="text-xs text-muted-foreground">
                {new Date(task.dueDate).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {expanded && (
          <TaskExpanded
            task={task}
            onStatusChange={onStatusChange}
            onGenerateChecklist={onGenerateChecklist}
            isGenerating={isGenerating}
          />
        )}
      </CardContent>
    </Card>
  );
}

function TaskExpanded({
  task,
  onStatusChange,
  onGenerateChecklist,
  isGenerating,
}: {
  readonly task: WorkflowTask;
  readonly onStatusChange: (status: string) => void;
  readonly onGenerateChecklist: () => void;
  readonly isGenerating: boolean;
}) {
  const queryClient = useQueryClient();

  const { data: checklistData } = useQuery({
    queryKey: ['workflow-checklist', task.taskId],
    queryFn: () => api.tasks.checklist(task.taskId),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ itemId, isCompleted }: { itemId: string; isCompleted: boolean }) =>
      api.tasks.updateChecklistItem(task.taskId, itemId, { isCompleted }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-checklist', task.taskId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-tasks', task.projectId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-project', task.projectId] });
    },
  });

  const items = checklistData?.items ?? [];

  return (
    <div className="border-t border-border px-4 pb-4 pt-3">
      {task.description && (
        <p className="mb-3 text-sm text-muted-foreground">{task.description}</p>
      )}

      {task.instructionsAi && (
        <div className="mb-3 rounded bg-purple-50/50 p-2 text-xs text-purple-900">
          <span className="font-medium">AI Instructions:</span> {task.instructionsAi}
        </div>
      )}

      {/* Actions */}
      <div className="mb-3 flex flex-wrap gap-2">
        {task.status === 'pending' && (
          <Button size="sm" variant="outline" onClick={() => onStatusChange('in_progress')}>
            Start Task
          </Button>
        )}
        {task.status === 'assigned' && (
          <Button size="sm" variant="outline" onClick={() => onStatusChange('in_progress')}>
            Start Task
          </Button>
        )}
        {task.status === 'in_progress' && (
          <Button size="sm" className="bg-teal text-white" onClick={() => onStatusChange('completed')}>
            <CheckCircle2 className="mr-1 h-3 w-3" /> Complete
          </Button>
        )}
        {items.length === 0 && (
          <Button size="sm" variant="outline" onClick={onGenerateChecklist} disabled={isGenerating}>
            <Sparkles className="mr-1 h-3 w-3" />
            {isGenerating ? 'Generating...' : 'AI Checklist'}
          </Button>
        )}
      </div>

      {/* Attestation */}
      {task.status === 'completed' && (
        <AttestationSection taskId={task.taskId} />
      )}

      {/* Checklist */}
      {items.length > 0 && (
        <div className="space-y-1">
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            Checklist ({items.filter((i) => i.isCompleted).length}/{items.length})
          </p>
          {items.map((item) => (
            <ChecklistRow
              key={item.itemId}
              item={item}
              onToggle={(checked) =>
                toggleMutation.mutate({ itemId: item.itemId, isCompleted: checked })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChecklistRow({
  item,
  onToggle,
}: {
  readonly item: WorkflowChecklistItem;
  readonly onToggle: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 rounded p-1.5 hover:bg-muted/50">
      <Checkbox
        checked={item.isCompleted}
        onCheckedChange={(checked) => onToggle(checked === true)}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <span className={cn('text-sm', item.isCompleted && 'text-muted-foreground line-through')}>
          {item.label}
        </span>
        {item.description && (
          <p className="text-xs text-muted-foreground">{item.description}</p>
        )}
        {item.requiresEvidence && !item.evidenceUrl && (
          <span className="mt-0.5 flex items-center gap-1 text-[10px] text-compliance-medium">
            <Shield className="h-3 w-3" /> Evidence required
          </span>
        )}
      </div>
    </label>
  );
}

function AttestationSection({ taskId }: { readonly taskId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['workflow-attestation', taskId],
    queryFn: () => api.tasks.getAttestation(taskId),
    retry: false,
  });

  if (isLoading) return null;
  if (!data) return null;

  const attestation = data as {
    attestationId: string;
    attestedBy: string;
    attestedAt: string;
    statement: string;
  };

  return (
    <div className="mb-3 rounded border border-compliance-low/30 bg-compliance-low/5 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-compliance-low">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Attested by {attestation.attestedBy} on{' '}
          {new Date(attestation.attestedAt).toLocaleDateString()}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 text-xs text-muted-foreground"
          onClick={() => window.open(api.reports.attestationPdf(taskId), '_blank')}
        >
          <Download className="mr-1 h-3 w-3" />
          PDF
        </Button>
      </div>
      {attestation.statement && (
        <p className="mt-1 text-xs italic text-muted-foreground">
          &ldquo;{attestation.statement}&rdquo;
        </p>
      )}
    </div>
  );
}
